import { useState, type FormEvent, type ReactNode } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { DialogOverlay, DialogTitle } from '../../components/ui/dialog'
import { DialogPortal } from '../../components/ui/dialog.lib'
import { Button } from '../../components/ui/button'
import { SignaturePad } from '../../components/ui/SignaturePad'
import { cn } from '../../lib/utils'
import { useAuthStore } from '../../store/auth'
import { useGateEnabled } from './useGateEnabled'
import {
  AgreementsError,
  acceptAgreement,
  fetchAcceptancePacket,
  fetchLegalIdentity,
  generateIdempotencyKey,
  type AcceptResult,
  type RequiredAgreementItem,
} from '../../api/agreements'
import { useRequiredAgreements } from './useRequiredAgreements'
import { TermsFullTextModal } from './TermsFullTextModal'
import { useLocaleStore } from '../../store/locale'
import { uiToAgreementLocale } from '../../i18n/agreement-locales'
import { useT } from '../../i18n'
import { termsAcceptanceI18n } from '../../i18n/onboarding/terms-acceptance'

const SUPPORT_EMAIL = 'support@pexify.net'

// ── Outer: wire router context → enabled flag ────────────────────────────────

export function TermsAcceptanceGate() {
  const navigate = useNavigate()
  // Only authenticated, non-admin seller/vendor context is gated (shared rule).
  const enabled = useGateEnabled()

  return (
    <TermsGateModal
      enabled={enabled}
      onLogout={() => navigate({ to: '/login' })}
    />
  )
}

// ── Inner: state machine (exported for tests, router-free) ───────────────────

export function TermsGateModal({
  enabled,
  onLogout,
}: {
  enabled: boolean
  onLogout?: () => void
}) {
  const { status, agreements, refetch } = useRequiredAgreements(enabled)
  const clearTokens = useAuthStore((s) => s.clearTokens)
  // A submit-time 503 flips the whole gate to the transient screen.
  const [forcedUnavailable, setForcedUnavailable] = useState(false)

  const effective = forcedUnavailable ? 'unavailable' : status
  const open = effective === 'signing' || effective === 'waiting' || effective === 'unavailable'

  const logout = () => {
    clearTokens()
    onLogout?.()
  }

  const retry = () => {
    setForcedUnavailable(false)
    refetch()
  }

  if (!open) return null

  return (
    <DialogPrimitive.Root open>
      <DialogPortal>
        <DialogOverlay className="bg-slate-900/60" />
        <DialogPrimitive.Content
          // Non-dismissible: no Escape, no overlay/outside click, no close X.
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid w-full max-w-[calc(100vw-1.5rem)] sm:max-w-lg',
            '-translate-x-1/2 -translate-y-1/2 gap-4 border border-slate-200 bg-white p-5 sm:p-6',
            'shadow-xl rounded-lg max-h-[calc(100dvh-1.5rem)] sm:max-h-[92vh] overflow-y-auto',
          )}
        >
          {effective === 'signing' && (
            <SigningScreen
              agreement={agreements[0]}
              onDone={refetch}
              onUnavailable={() => setForcedUnavailable(true)}
              footer={<GateFooter onLogout={logout} />}
            />
          )}
          {effective === 'waiting' && (
            <WaitingScreen agreement={agreements[0]} footer={<GateFooter onLogout={logout} />} />
          )}
          {effective === 'unavailable' && (
            <UnavailableScreen onRetry={retry} footer={<GateFooter onLogout={logout} />} />
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  )
}

// ── Shared footer: logout + support (reachable in every state) ───────────────

function GateFooter({ onLogout }: { onLogout: () => void }) {
  const t = useT(termsAcceptanceI18n).footer
  return (
    <div className="mt-2 flex flex-col gap-2 border-t border-slate-100 pt-3 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
      <span>
        {t.needHelp}{' '}
        <a className="text-brand-700 underline" href={`mailto:${SUPPORT_EMAIL}`}>
          {t.contactSupport}
        </a>
      </span>
      <button type="button" onClick={onLogout} className="text-left text-slate-500 underline sm:text-right">
        {t.logOut}
      </button>
    </div>
  )
}

// ── Waiting (required, but current user is not authorized to sign) ───────────

function WaitingScreen({
  agreement,
  footer,
}: {
  agreement: RequiredAgreementItem | undefined
  footer: ReactNode
}) {
  const t = useT(termsAcceptanceI18n).waiting
  return (
    <div className="flex flex-col gap-3">
      <DialogTitle>{t.title}</DialogTitle>
      <p className="text-sm text-slate-600">
        {t.mustAcceptBefore}
        <span className="font-medium text-slate-800">{agreement?.title ?? t.fallbackAgreement}</span>
        {t.mustAcceptAfter}
      </p>
      <p className="text-sm text-slate-600">{t.waitingNote}</p>
      {footer}
    </div>
  )
}

// ── Unavailable (transient 503 — retryable, NOT a permanent block) ───────────

function UnavailableScreen({
  onRetry,
  footer,
}: {
  onRetry: () => void
  footer: ReactNode
}) {
  const t = useT(termsAcceptanceI18n).unavailable
  return (
    <div className="flex flex-col gap-3">
      <DialogTitle>{t.title}</DialogTitle>
      <p className="text-sm text-slate-600">{t.body}</p>
      <div>
        <Button type="button" variant="primary" onClick={onRetry}>
          {t.retry}
        </Button>
      </div>
      {footer}
    </div>
  )
}

// ── Signing (required + current user authorized) ─────────────────────────────

type FormErrors = Partial<
  Record<
    | 'signerFullName'
    | 'orgLegalName'
    | 'orgCountry'
    | 'authority'
    | 'electronic'
    | 'program'
    | 'signature'
    | 'password',
    string
  >
>

function SigningScreen({
  agreement,
  onDone,
  onUnavailable,
  footer,
}: {
  agreement: RequiredAgreementItem | undefined
  onDone: () => void
  onUnavailable: () => void
  footer: ReactNode
}) {
  const dict = useT(termsAcceptanceI18n)
  const t = dict.signing
  const te = dict.errors
  const identityQ = useQuery({
    queryKey: ['legal-identity'],
    queryFn: fetchLegalIdentity,
  })

  const [textOpen, setTextOpen] = useState(false)
  const uiLocale = useLocaleStore((s) => s.locale)
  // The language the signer reads + binds their acceptance to (defaults to the
  // UI preference; switchable in the full-text modal).
  const [signLocale, setSignLocale] = useState(() => uiToAgreementLocale(uiLocale))
  const [signerFullName, setSignerFullName] = useState('')
  const [signerTitle, setSignerTitle] = useState('')
  // null sentinel = "untouched → show the prefill"; once the user edits (even to
  // empty), the field holds a real string and is fully clearable. This prefills
  // from the loaded identity without an effect (no setState-in-effect).
  const [legalNameInput, setLegalNameInput] = useState<string | null>(null)
  const [countryInput, setCountryInput] = useState<string | null>(null)
  const [orgAddress, setOrgAddress] = useState('')
  const [orgState, setOrgState] = useState('')
  const [authority, setAuthority] = useState(false)
  const [electronic, setElectronic] = useState(false)
  const [program, setProgram] = useState(false)
  const [signature, setSignature] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState<FormErrors>({})
  const [conflict, setConflict] = useState(false)
  const [result, setResult] = useState<AcceptResult | null>(null)

  const identity = identityQ.data
  const orgLegalName = legalNameInput ?? identity?.suggested_legal_name ?? ''
  const orgCountry = countryInput ?? (identity?.country ?? '').toUpperCase()
  const entityLabel = orgLegalName || identity?.name || t.fallbackEntity

  const mutation = useMutation({
    mutationFn: () => {
      if (!agreement) throw new Error('no agreement')
      return acceptAgreement(agreement.agreement_id, {
        authority_attested: authority,
        electronic_consent: electronic,
        program_acknowledgement: program,
        signer_full_name: signerFullName.trim(),
        signer_title: signerTitle.trim() || null,
        org_legal_name: orgLegalName.trim(),
        org_country: orgCountry.trim().toUpperCase(),
        org_state: orgState.trim() || null,
        // NOTE: org address is captured for the operator's context but is not
        // part of the accept contract (extra="forbid"), so it is not sent.
        signature: signature ?? '',
        current_password: password,
        idempotency_key: generateIdempotencyKey(),
        accepted_locale: signLocale,
      })
    },
    onSuccess: (res) => setResult(res),
    onError: (err) => {
      if (err instanceof AgreementsError && err.isUnavailable) {
        onUnavailable()
        return
      }
      if (err instanceof AgreementsError && err.isIdempotencyConflict) {
        setConflict(true)
        onDone()
        return
      }
      // Server-side validation → surface on the form.
      setErrors((prev) => ({
        ...prev,
        password: err instanceof AgreementsError ? err.message : te.submissionFailed,
      }))
    },
  })

  const validate = (): boolean => {
    const next: FormErrors = {}
    if (!signerFullName.trim()) next.signerFullName = te.fullNameRequired
    if (!orgLegalName.trim()) next.orgLegalName = te.legalNameRequired
    if (orgCountry.trim().length !== 2) next.orgCountry = te.countryRequired
    if (!authority) next.authority = te.required
    if (!electronic) next.electronic = te.required
    if (!program) next.program = te.required
    if (!signature) next.signature = te.signatureRequired
    if (!password.trim()) next.password = te.passwordRequired
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setConflict(false)
    if (!validate()) return
    mutation.mutate()
  }

  if (result) {
    return <SuccessScreen result={result} onContinue={onDone} footer={footer} />
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
      <div className="flex flex-col gap-1">
        <DialogTitle>{t.title}</DialogTitle>
        <p className="text-sm text-slate-600">
          {t.introBefore(entityLabel)}
          <button
            type="button"
            className="font-medium text-brand-700 underline"
            onClick={() => setTextOpen(true)}
          >
            {agreement?.title ?? t.fallbackAgreement}
          </button>
          {t.introAfter}
        </p>
      </div>

      {conflict && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {t.conflict}
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t.fullNameLabel} required error={errors.signerFullName}>
          <input
            className={inputCls}
            value={signerFullName}
            onChange={(e) => setSignerFullName(e.target.value)}
            aria-label={t.fullNameLabel}
          />
        </Field>
        <Field label={t.signerTitleLabel} error={undefined}>
          <input
            className={inputCls}
            value={signerTitle}
            onChange={(e) => setSignerTitle(e.target.value)}
            placeholder={t.signerTitlePlaceholder}
            aria-label={t.signerTitleLabel}
          />
        </Field>
        <Field label={t.legalNameLabel} required error={errors.orgLegalName}>
          <input
            className={inputCls}
            value={orgLegalName}
            onChange={(e) => setLegalNameInput(e.target.value)}
            aria-label={t.legalNameLabel}
          />
        </Field>
        <Field label={t.addressLabel} error={undefined}>
          <input
            className={inputCls}
            value={orgAddress}
            onChange={(e) => setOrgAddress(e.target.value)}
            aria-label={t.addressLabel}
          />
        </Field>
        <Field label={t.countryLabel} required error={errors.orgCountry}>
          <input
            className={cn(inputCls, 'uppercase')}
            value={orgCountry}
            maxLength={2}
            onChange={(e) => setCountryInput(e.target.value.toUpperCase())}
            placeholder="US"
            aria-label={t.countryLabel}
          />
        </Field>
        <Field label={t.stateLabel} error={undefined}>
          <input
            className={inputCls}
            value={orgState}
            onChange={(e) => setOrgState(e.target.value)}
            aria-label={t.stateLabel}
          />
        </Field>
      </div>

      <fieldset className="flex flex-col gap-2">
        <CheckboxRow
          checked={authority}
          onChange={setAuthority}
          error={errors.authority}
          label={t.authorityConsent(entityLabel)}
        />
        <CheckboxRow
          checked={electronic}
          onChange={setElectronic}
          error={errors.electronic}
          label={t.electronicConsent}
        />
        <CheckboxRow
          checked={program}
          onChange={setProgram}
          error={errors.program}
          label={t.programConsent}
        />
      </fieldset>

      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-slate-700">
          {t.signatureLabel} <span className="text-rose-600">*</span>
        </span>
        <SignaturePad onChange={setSignature} />
        {errors.signature && <span className="text-xs text-rose-600">{errors.signature}</span>}
      </div>

      <Field label={t.passwordLabel} required error={errors.password}>
        <input
          type="password"
          className={inputCls}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          aria-label={t.passwordLabel}
        />
      </Field>

      <Button type="submit" variant="primary" disabled={mutation.isPending}>
        {mutation.isPending ? t.submitting : t.submit}
      </Button>

      {footer}

      {agreement && (
        <TermsFullTextModal
          agreementId={agreement.agreement_id}
          title={agreement.title}
          open={textOpen}
          onOpenChange={setTextOpen}
          locale={signLocale}
          onLocaleChange={setSignLocale}
        />
      )}
    </form>
  )
}

// ── Success ──────────────────────────────────────────────────────────────────

function SuccessScreen({
  result,
  onContinue,
  footer,
}: {
  result: AcceptResult
  onContinue: () => void
  footer: ReactNode
}) {
  const t = useT(termsAcceptanceI18n).success
  const [downloadError, setDownloadError] = useState(false)

  const download = async () => {
    setDownloadError(false)
    try {
      const packet = await fetchAcceptancePacket(result.acceptance_id)
      window.open(packet.download_url, '_blank', 'noopener')
    } catch {
      setDownloadError(true)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <DialogTitle>{t.title}</DialogTitle>
      <p className="text-sm text-slate-600">{t.body}</p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button type="button" variant="outline" onClick={download}>
          {t.download}
        </Button>
        <Button type="button" variant="primary" onClick={onContinue}>
          {t.continue}
        </Button>
      </div>
      {downloadError && (
        <span className="text-xs text-rose-600">
          {t.downloadError}
        </span>
      )}
      {footer}
    </div>
  )
}

// ── Small form atoms ─────────────────────────────────────────────────────────

const inputCls =
  'w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500'

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string
  required?: boolean
  error?: string
  children: ReactNode
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-slate-700">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      {children}
      {error && <span className="text-xs text-rose-600">{error}</span>}
    </label>
  )
}

function CheckboxRow({
  checked,
  onChange,
  label,
  error,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  error?: string
}) {
  return (
    <label className="flex items-start gap-2 text-sm text-slate-700">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 shrink-0"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        {label}
        {error && <span className="ml-1 text-xs text-rose-600">{error}</span>}
      </span>
    </label>
  )
}
